/**
 * Shared helpers for `AutoTuneJobDagContainer` and the
 * `AutoTuneSettingsDrawer` DAG slot.
 *
 * Extracted so `AutoTuneJobDagContainer` stays under the 250-line limit.
 */
import type { DbxAutoRecommendationItem } from "@/pages/jobs/types";
import type { AutoTuneTaskNodeData, ClusterSpec, TaskNodeStatus } from "./AutoTuneJobDag.types";

// Card width is sized to fit the Worker/Driver instance names on a
// single line — the cluster type never wraps, truncates, or overflows.
// The same width is fed to BOTH the rendered card and the dagre layout
// (see ``AutoTuneJobDag``) so the layout box matches what's drawn and
// adjacent ranks never overlap.
//
// Instance-name width is MEASURED with the canvas Text Metrics API at the
// value Typography's real font (proportional glyphs make per-character
// estimates inaccurate). The remaining constants are fixed layout
// geometry — paddings, the label gutter, the arrow column — not text
// estimates. The clamp keeps short cards compact and bounds pathological
// names.
const LABEL_COL_PX = 56; // "Worker" / "Driver" gutter + gap
const PANEL_PAD_PX = 24; // ClusterPanel ``al-px-3`` (both sides)
const CARD_PAD_PX = 24; // CardContent ``!al-p-3`` (both sides)
const ARROW_COL_PX = 28; // arrow icon + flanking gaps
const META_FLOOR_PX = 150; // worker-count + cost line ("10-14 workers · $72.00/hr")
const TEXT_SAFETY_PX = 4; // sub-pixel headroom so a measured name never overflows
const FALLBACK_CHAR_PX = 9.5; // jest/SSR only: no canvas TextMetrics there
export const MIN_NODE_WIDTH = 360;
export const MAX_NODE_WIDTH = 680;

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

// Value font for the Worker/Driver instance text — the ``body-sm`` /
// ``medium`` Typography token in the app font. Measuring at this font
// matches what the browser renders (verified) without per-character
// estimation. It's re-applied on every measure so a fresh reading can't
// inherit a stale value taken before the web font finished loading.
const VALUE_FONT = "500 14px Lexend, sans-serif";

// Cached 2D context (the canvas element only, not the font/metrics).
// ``null`` when canvas/DOM is unavailable (jest/SSR) — callers fall back.
let measureCtx: CanvasRenderingContext2D | null | undefined;

const getMeasureCtx = (): CanvasRenderingContext2D | null => {
  if (measureCtx !== undefined) return measureCtx;
  try {
    measureCtx = document.createElement("canvas").getContext("2d");
  } catch {
    measureCtx = null;
  }
  return measureCtx;
};

// Drop the cached context. Called by ``AutoTuneJobDag`` once the web font
// finishes loading (so the next layout re-measures with real glyph
// metrics) and by tests to re-mock ``getContext`` between cases.
export const resetMeasureCache = (): void => {
  measureCtx = undefined;
};

// Rendered pixel width of one instance label. Canvas text metrics in the
// browser; under jest/SSR (no canvas) a generous char estimate keeps
// layout deterministic without crashing.
const measureValueWidth = (value: string): number => {
  if (!value) return 0;
  const ctx = getMeasureCtx();
  if (!ctx) return value.length * FALLBACK_CHAR_PX;
  ctx.font = VALUE_FONT;
  const width = ctx.measureText(value).width;
  return Number.isFinite(width) && width > 0
    ? width + TEXT_SAFETY_PX
    : value.length * FALLBACK_CHAR_PX;
};

// Width one Current/Proposed panel needs to fit its longest instance
// label on a single line.
const panelWidth = (spec: ClusterSpec): number => {
  const worker = spec.workerInstance ?? spec.instance ?? "";
  const driver = spec.driverInstance ?? worker;
  const valueLine = LABEL_COL_PX + Math.max(measureValueWidth(worker), measureValueWidth(driver));
  return PANEL_PAD_PX + Math.max(valueLine, META_FLOOR_PX);
};

// Single-line width for an Auto Tune task card. ``no_change`` cards have
// a single full-width panel and short names, so they stay at the min;
// actionable cards reserve room for two equal panels + the arrow.
export const computeNodeWidth = (node: AutoTuneTaskNodeData): number => {
  if (node.status === "no_change") return MIN_NODE_WIDTH;
  const col = Math.max(panelWidth(node.current), panelWidth(node.proposed));
  return clamp(Math.round(CARD_PAD_PX + 2 * col + ARROW_COL_PX), MIN_NODE_WIDTH, MAX_NODE_WIDTH);
};

// ``recommended_driver_instance: null`` from the BE means "do not
// change the driver" — apply leaves the live driver untouched. Falling
// back to the recommended worker would fabricate a driver diff that
// won't survive apply. Current driver still falls back to current
// worker (Databricks's common single-node-type cluster case).
const resolveCurrentWorker = (rec: DbxAutoRecommendationItem): string | null =>
  rec.current_worker_node_type ?? null;

const resolveCurrentDriver = (rec: DbxAutoRecommendationItem): string | null =>
  rec.current_driver_node_type ?? rec.current_worker_node_type ?? null;

const resolveRecommendedWorker = (rec: DbxAutoRecommendationItem): string | null =>
  rec.recommended_worker_instance ?? null;

const resolveRecommendedDriver = (rec: DbxAutoRecommendationItem): string | null =>
  rec.recommended_driver_instance ?? null;

export const deriveStatus = (rec: DbxAutoRecommendationItem): TaskNodeStatus => {
  if (rec.auto_tune_mode === "enabled") {
    return rec.latest_action_event_type === "APPLIED" ? "applied" : "pending_apply";
  }
  const currentWorker = resolveCurrentWorker(rec);
  const currentDriver = resolveCurrentDriver(rec);
  const recWorker = resolveRecommendedWorker(rec) ?? currentWorker;
  const recDriver = resolveRecommendedDriver(rec) ?? currentDriver;
  const proposedMin = rec.recommended_min_workers ?? rec.current_min_workers ?? null;
  const proposedMax = rec.recommended_max_workers ?? rec.current_max_workers ?? null;
  const proposalDiffers =
    recWorker !== currentWorker ||
    recDriver !== currentDriver ||
    proposedMin !== (rec.current_min_workers ?? null) ||
    proposedMax !== (rec.current_max_workers ?? null);
  const hasSavings = (rec.monthly_savings_usd ?? 0) > 0 || (rec.monthly_savings_max_usd ?? 0) > 0;
  if (proposalDiffers || hasSavings) {
    return "recommended";
  }
  return "no_change";
};

// Build a no_change node from a job task that doesn't have an Auto
// Tune recommendation. Surfaces the task in the DAG with the same
// visual treatment as a no_change rec (muted card, no proposed
// panel). Cluster spec is optional — when the inventory endpoint
// resolves a current cluster (via the latest run's compute_ids),
// the card renders the Current panel with worker/driver/min/max;
// otherwise the panel collapses to a "no recommendation" placeholder.
export interface NoRecClusterSpec {
  workerNodeType: string | null;
  driverNodeType: string | null;
  minWorkers: number | null;
  maxWorkers: number | null;
}

const EMPTY_NO_REC_SPEC: NoRecClusterSpec = {
  workerNodeType: null,
  driverNodeType: null,
  minWorkers: null,
  maxWorkers: null,
};

// True when the inventory JOIN returned at least one current-cluster
// field. The card render uses this to decide between the Current
// panel and the "no recommendation" placeholder, so partial data
// (e.g. min/max workers without an instance type) still renders
// instead of being silently dropped.
export const hasAnyCurrentSpec = (spec: AutoTuneTaskNodeData["current"]): boolean =>
  Boolean(
    spec.workerInstance ||
    spec.driverInstance ||
    spec.min != null ||
    spec.max != null ||
    (spec.instance && spec.instance !== "—")
  );

export const buildNoRecNode = (
  taskKey: string,
  locked: boolean,
  lockedMessage: string | null,
  cluster: NoRecClusterSpec = EMPTY_NO_REC_SPEC
): AutoTuneTaskNodeData => {
  const worker = cluster.workerNodeType;
  const driver = cluster.driverNodeType ?? worker;
  // ``current`` and ``proposed`` get distinct objects so a future
  // mutation to one doesn't leak into the other.
  const baseSpec = {
    instance: worker ?? "—",
    workerInstance: worker,
    driverInstance: driver,
    min: cluster.minWorkers,
    max: cluster.maxWorkers,
    // ``null`` = no priced source. ``AutoTuneTaskNode.parts`` skips
    // the cost row when null so non-rec cards don't show "$0.00/hr".
    costPerHr: null,
  };
  return {
    id: taskKey,
    taskKey,
    current: { ...baseSpec },
    proposed: { ...baseSpec },
    status: "no_change",
    enabled: false,
    reasoning: null,
    monthlySavingsUsd: null,
    monthlySavingsMaxUsd: null,
    monthlyDbuSavingsUsd: null,
    toggling: false,
    locked,
    lockedMessage,
  };
};

/** Aggregate selected savings for the drawer's "Potential Savings for
 *  selected Tasks" tile.
 *
 *  - ``low`` / ``high`` drive the headline dollar range; ``low`` is a
 *    display convention (high * 0.8) mirroring the per-task cost
 *    strip — not a server-computed bound. Totals are intentional here
 *    (DBU + infra) so the user sees the full realizable savings, not
 *    just the DBU portion.
 *  - ``dbu`` is the apples-to-apples numerator for the "% of job cost"
 *    hint — divides cleanly into the DBU-only ``Est. Cost``. Returns
 *    ``null`` when ANY selected actionable task lacks DBU metadata.
 *    Summing only the known subset would silently understate the
 *    percent while the dollar headline includes the full set — same
 *    apples-to-oranges shape as the >100% bug, just less visible. The
 *    drawer hides the hint in that mixed state. */
export const sumSelectedSavings = (
  nodes: AutoTuneTaskNodeData[],
  selectedSet: Set<string>
): { low: number; high: number; dbu: number | null } => {
  let high = 0;
  let dbu = 0;
  let anyNullDbu = false;
  for (const n of nodes) {
    if (!selectedSet.has(n.taskKey) || n.status === "no_change") continue;
    high += Math.max(n.monthlySavingsUsd ?? 0, n.monthlySavingsMaxUsd ?? 0);
    if (n.monthlyDbuSavingsUsd == null) {
      anyNullDbu = true;
    } else {
      dbu += n.monthlyDbuSavingsUsd;
    }
  }
  return { low: high * 0.8, high, dbu: anyNullDbu ? null : dbu };
};

/** Aggregate "static" potential savings across actionable recs.
 *  Sums max-savings per rec; the banner range is ``min = max * 0.8``
 *  to mirror the per-card cost strip's conservative low-end. Used by
 *  the drawer banner AND the container — extracted here to keep the
 *  two sources in sync. Cent precision is preserved so sub-$1/mo
 *  opportunities (short, high-frequency tasks) don't round away to
 *  $0 in the headline.
 *
 *  ``monthly_savings_usd`` already reflects each task's actual
 *  30-day run-hours (BE-side rescale), so summing the raw rec values
 *  here yields a realistic projection — no FE rescale needed. */
export const calculateBannerSavings = (
  recommendations: DbxAutoRecommendationItem[]
): { potentialMin: number; potentialMax: number } => {
  const actionableRecs = recommendations.filter(r => deriveStatus(r) !== "no_change");
  let highSum = 0;
  actionableRecs.forEach(r => {
    const h = Math.max(r.monthly_savings_usd ?? 0, r.monthly_savings_max_usd ?? 0);
    highSum += h;
  });
  return {
    potentialMin: highSum * 0.8,
    potentialMax: highSum,
  };
};

export const buildNode = (
  rec: DbxAutoRecommendationItem,
  locked: boolean,
  lockedMessage: string | null
): AutoTuneTaskNodeData => {
  const taskKey = rec.task_key ?? "";
  const id = taskKey !== "" ? taskKey : (rec.entity_name ?? "—");
  const currentWorker = resolveCurrentWorker(rec);
  const currentDriver = resolveCurrentDriver(rec);
  const recWorker = resolveRecommendedWorker(rec) ?? currentWorker;
  const recDriver = resolveRecommendedDriver(rec) ?? currentDriver;
  return {
    id,
    taskKey,
    current: {
      // ``instance`` mirrors the worker for legacy consumers; the
      // worker/driver fields drive the new two-line render.
      instance: currentWorker ?? "—",
      workerInstance: currentWorker,
      driverInstance: currentDriver,
      min: rec.current_min_workers ?? null,
      max: rec.current_max_workers ?? null,
      costPerHr: rec.current_cost_per_hr ?? 0,
    },
    proposed: {
      instance: recWorker ?? currentWorker ?? "—",
      workerInstance: recWorker,
      driverInstance: recDriver,
      min: rec.recommended_min_workers ?? rec.current_min_workers ?? null,
      max: rec.recommended_max_workers ?? rec.current_max_workers ?? null,
      costPerHr: rec.recommended_cost_per_hr ?? rec.current_cost_per_hr ?? 0,
    },
    status: deriveStatus(rec),
    enabled: rec.auto_tune_mode === "enabled",
    reasoning: rec.reasoning ?? null,
    monthlySavingsUsd: rec.monthly_savings_usd ?? null,
    monthlySavingsMaxUsd: rec.monthly_savings_max_usd ?? null,
    monthlyDbuSavingsUsd: rec.monthly_dbu_savings_usd ?? null,
    toggling: false,
    locked,
    lockedMessage,
  };
};
